import { useQueryClient } from '@tanstack/react-query'
import { type VFC } from 'react'
import { StaticRouter } from 'react-router-dom/server'
import {
  optimisticallyUpdateMomentForAccess,
  updateMomentForPersistedAccess,
  useReadMomentAccess,
  useUpdateMomentAccess,
} from '@opallabs/api'
import { type SimpleWorkspace } from '@opallabs/concepts'
import { type Theme } from '@opallabs/gem'
import { DefaultQueryClientProvider } from '@opallabs/higgs'
import { ThemeProvider, WorkspaceContext } from '@opallabs/hooks'
import {
  ensureOwnerIsInAccess,
  ResourceShareModal,
} from '@opallabs/permissions'

type MomentShareModalProps = Readonly<{
  legacyMomentId: string
  momentTitle: string
  momentUuid: string
  open: boolean
  onClose: () => void
  ownerUuid?: string
  readOnly?: boolean
}>

const MomentShareModal: VFC<MomentShareModalProps> = ({
  legacyMomentId,
  momentTitle,
  momentUuid,
  onClose,
  open,
  ownerUuid,
  readOnly = false,
}) => {
  const queryClient = useQueryClient()
  const unmodifiedAccess = useReadMomentAccess({
    momentUuid,
    enabled: open,
    // Refetch on open so the modal reflects latest permissions
    refetchOnMount: 'always',
  })
  const currentAccess = ensureOwnerIsInAccess(unmodifiedAccess, ownerUuid)

  const updateMomentAccess = useUpdateMomentAccess({
    onMutate: (mutationParams) => {
      optimisticallyUpdateMomentForAccess(queryClient, mutationParams)
    },
    onSuccess: (data) => {
      updateMomentForPersistedAccess(queryClient, data)
    },
  })

  return (
    <ResourceShareModal
      isVisible={open}
      onClose={onClose}
      metadataQuery={currentAccess}
      onChangeAccess={async (access) => {
        await updateMomentAccess.mutateAsync({ access, id: momentUuid })
      }}
      ownerId={ownerUuid}
      readOnly={readOnly}
      resourceId={momentUuid}
      resourceShareParams={{
        legacyMomentId: String(legacyMomentId),
      }}
      resourceTitle={momentTitle}
      resourceType="moment"
    />
  )
}

export const QueryableMomentShareModal: VFC<
  MomentShareModalProps & { workspace: SimpleWorkspace; theme?: Theme }
> = ({ workspace, theme = 'dark', ...props }) => {
  const location = window.top?.location ?? window.location
  return (
    <DefaultQueryClientProvider>
      <StaticRouter location={location.pathname}>
        <WorkspaceContext.Provider value={workspace}>
          <ThemeProvider theme={theme}>
            <MomentShareModal {...props} />
          </ThemeProvider>
        </WorkspaceContext.Provider>
      </StaticRouter>
    </DefaultQueryClientProvider>
  )
}
